import React, { Component } from "react";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import axios from "axios";
import Auth from "../../Auth";
import Loading from "../loading";
import { link, downloadFile, numb2 } from "../link.js";
import {
  FIRST_PAGE,
  PAGE_STEP,
  toNumber,
  matchesSearch,
  ReportLayout,
  ReportCard,
  ReportFilters,
  ReportField,
  RoundSelect,
  SearchField,
  ReportSummary,
  ReportEmpty,
  ReportTable,
  ReportFooter,
} from "./reportParts";

const toDateTime = (date) => (date ? format(date, "yyyy-MM-dd HH:mm:ss") : "");

const COLUMNS = [
  { label: "#" },
  { label: "ID" },
  { label: "Transaction date" },
  { label: "Payer/Payee" },
  { label: "Category" },
  { label: "Transaction code" },
  { label: "Description" },
  { label: "Bank" },
  { label: "Money in", numeric: true },
  { label: "Money out", numeric: true },
  { label: "Bank fees", numeric: true },
];

export class Cashbook extends Component {
  constructor(props) {
    super(props);
    this.state = {
      rounds: [],
      report: null,
      filters: { roundid: "", bank: "BPR", name: "", datefrom: "", dateTo: "" },
      startDate: null,
      endDate: null,
      loading: false,
      dataOut: FIRST_PAGE,
    };
    this.mounted = false;
  }

  componentDidMount() {
    this.mounted = true;
    this.getRounds();
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  getRounds = () => {
    fetch(link() + `&do=getshort&ra=round&ft=areclist&elmt=roundStatus&id=Active`, {
      method: "post",
      headers: { "Content-Type": "application/json" },
    })
      .then((response) => response.json())
      .then((response) => {
        if (this.mounted) this.setState({ rounds: Array.isArray(response) ? response : [] });
      })
      .catch((err) => {
        console.log("Error: ", err);
        Auth.handleHttpCodes(err, "cashbook");
      });
  };

  requestBody = (filters) => ({
    datefrom: filters.datefrom,
    dateTo: filters.dateTo,
    userid: "",
    memberid: "",
    roundid: filters.roundid,
    roundname: "",
    name: filters.name,
    bank: filters.bank,
    termstartdate: "",
    termduedate: "",
    status: "",
  });

  getReport = (filters) => {
    this.setState({ loading: true });
    localStorage.setItem("currentView", "income");
    axios
      .post(link() + `&do=get&ra=report&rec=cb`, JSON.stringify(this.requestBody(filters)), {
        headers: { "Content-Type": "application/json" },
        timeout: 90000,
      })
      .then((res) => {
        if (this.mounted) {
          this.setState({ report: res.data && typeof res.data === "object" ? res.data : {}, dataOut: FIRST_PAGE, loading: false });
        }
      })
      .catch((err) => {
        console.log("Error: ", err);
        Auth.handleHttpCodes(err, "cashbook");
        if (this.mounted) this.setState({ loading: false });
      });
  };

  updateFilters = (changes) => {
    const filters = { ...this.state.filters, ...changes };
    this.setState({ filters });
    this.getReport(filters);
  };

  handleRoundSelect = (e) => this.updateFilters({ roundid: e.target.value });
  handleBankSelect = (e) => this.updateFilters({ bank: e.target.value });
  handleSearch = (e) => this.setState({ filters: { ...this.state.filters, name: e.target.value }, dataOut: FIRST_PAGE });
  handleChangeDate = (date) => {
    this.setState({ startDate: date, dataOut: FIRST_PAGE, filters: { ...this.state.filters, datefrom: toDateTime(date) } });
  };
  handleChangeEndDate = (date) => {
    this.setState({ endDate: date, dataOut: FIRST_PAGE, filters: { ...this.state.filters, dateTo: toDateTime(date) } });
  };
  loadMore = () => this.setState((state) => ({ dataOut: state.dataOut + PAGE_STEP }));

  download = () => {
    downloadFile(
      "POST",
      link() + `&do=get&ra=report&rec=cb` + `&ufile=getfile`,
      this.requestBody(this.state.filters),
      { "Content-Type": "application/application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" },
      "cashbook.xlsx"
    );
  };

  // The date range and payer search narrow the transactions the backend already returned for the round.
  filteredTransactions = () => {
    const { report, filters } = this.state;
    const rows = report && Array.isArray(report.attachment) ? report.attachment : [];
    const from = filters.datefrom.substring(0, 10);
    const to = filters.dateTo.substring(0, 10);
    return rows.filter((item) => {
      const day = (item.transDate || "").toString().substring(0, 10);
      return matchesSearch(item.names, filters.name) && (!from || day >= from) && (!to || day <= to);
    });
  };

  render() {
    const { rounds, report, filters, startDate, endDate, loading, dataOut } = this.state;
    const transactions = this.filteredTransactions();
    const visible = transactions.slice(0, dataOut);
    const totals = transactions.reduce(
      (sum, item) => ({
        moneyIn: sum.moneyIn + toNumber(item.moneyIn),
        moneyOut: sum.moneyOut + toNumber(item.moneyOut),
        fees: sum.fees + toNumber(item.transactionFee),
      }),
      { moneyIn: 0, moneyOut: 0, fees: 0 }
    );
    const opening = toNumber(report && report.startAmount);
    const inOut = totals.moneyIn - totals.moneyOut;
    const closing = opening + inOut - totals.fees;
    const currency = (report && report.currency) || "";
    const money = (value) => `${currency} ${numb2(value)}`;

    return (
      <ReportLayout>
        <ReportCard
          title="Cashbook Report:"
          subtitle={report && report.roundName}
          onDownload={transactions.length > 0 ? this.download : undefined}
          downloadLabel="Download cashbook"
        >
          <ReportFilters>
            <RoundSelect rounds={rounds} value={filters.roundid} onChange={this.handleRoundSelect} />
            <ReportField label="Account">
              <select value={filters.bank} onChange={this.handleBankSelect} aria-label="Select account">
                <option value="">All accounts</option>
                <option value="BPR">BPR</option>
                <option value="COPEDU">COPEDU</option>
                <option value="Momo">Momo</option>
              </select>
            </ReportField>
            <ReportField label="Date from">
              <DatePicker selected={startDate} onChange={this.handleChangeDate} className="report-input" dateFormat="yyyy-MM-dd" placeholderText="Pick a date" isClearable />
            </ReportField>
            <ReportField label="Date to">
              <DatePicker selected={endDate} onChange={this.handleChangeEndDate} className="report-input" dateFormat="yyyy-MM-dd" placeholderText="Pick a date" isClearable />
            </ReportField>
            <SearchField label="Payer" value={filters.name} onChange={this.handleSearch} />
          </ReportFilters>

          {loading && <Loading />}
          {!loading && !report && <ReportEmpty>Select a round to see its cashbook.</ReportEmpty>}
          {!loading && report && (
            <>
              <ReportSummary
                items={[
                  { label: "Bank", value: report.searchbank || "All accounts" },
                  { label: "Cash at the beginning", value: money(opening) },
                  { label: "Money in", value: money(totals.moneyIn) },
                  { label: "Money out", value: money(totals.moneyOut) },
                  { label: "Transaction fees", value: money(totals.fees) },
                  { label: "In - Out", value: money(inOut) },
                  { label: "Balance", value: money(closing), highlight: true },
                ]}
              />
              {transactions.length === 0 ? (
                <ReportEmpty>No transactions match the selected filters.</ReportEmpty>
              ) : (
                <>
                  <ReportTable columns={COLUMNS}>
                    {visible.map((item, index) => (
                      <tr key={`${item.recordID}-${index}`}>
                        <td>{index + 1}</td>
                        <td>{item.recordID}</td>
                        <td className="nowrap">{(item.transDate || "").toString().substring(0, 10)}</td>
                        <td>{item.names}</td>
                        <td>{item.category}</td>
                        <td className="nowrap">{item.transactionReference}</td>
                        <td className="wide">{item.remark}</td>
                        <td>
                          {item.bankName}
                          {item.bankType && <small className="report-sub">{item.bankType}</small>}
                        </td>
                        <td className="num">{numb2(item.moneyIn)}</td>
                        <td className="num">{numb2(item.moneyOut)}</td>
                        <td className="num">{numb2(item.transactionFee)}</td>
                      </tr>
                    ))}
                  </ReportTable>
                  <ReportFooter shown={visible.length} total={transactions.length} onMore={this.loadMore} />
                </>
              )}
            </>
          )}
        </ReportCard>
      </ReportLayout>
    );
  }
}

export default Cashbook;
